// Lagenoverzicht van de labopdracht: alle lagen van de boringen als kaartjes, met een filter
// dat per laag zegt waaróm hij afvalt, en een voorstel voor mengmonsters volgens de mengregels.
//
// Bewust los van de component: het filter en de mengregels zijn de plek waar het fout kan gaan
// (een asbestverdachte pot in een mengmonster), en dat wil je kunnen testen zonder scherm.
import {
  BoringModel, GEUR_TYPE, OLIE_WATER_REACTIE, getBzbGradationLabel, classifyBzbLabel, bzbFillsLayer,
  MONSTER_VERPAKKING, monsterVerpakkingLabel,
} from '@mv/models';
import { buildBoorprofielData } from './boorprofiel-data.js';

/**
 * Soort verpakking van een potje, voor het filter: pot, steekbus, emmer, zak, fles of vluchtig.
 *
 * Grover dan de verpakkingscode zelf (glazen pot 580 ml, steekbus 50 mm, emmer 5 l): bij het
 * samenstellen wil je 'alle steekbussen' of 'de emmers apart', niet één maat. De codelijst
 * groepeert emmers en zakken samen als 'Bulk'; hier staan ze los, want een emmer asfalt en een
 * zak grond zijn voor het lab twee verschillende dingen.
 */
export const PACKAGING_LABELS = {
  pot: 'Pot', steekbus: 'Steekbus', emmer: 'Emmer', zak: 'Zak', fles: 'Fles', vluchtig: 'Vluchtig',
  overig: 'Overig', onbekend: 'Onbekend',
};
const GROUP_PACKAGING = { Pot: 'pot', Ongeroerd: 'steekbus', Fles: 'fles', Vluchtig: 'vluchtig', Overig: 'overig' };
const PACKAGING_OF_CODE = new Map(MONSTER_VERPAKKING.map((v) => [v.code,
  v.group === 'Bulk' ? (/emmer/i.test(v.label) ? 'emmer' : 'zak') : GROUP_PACKAGING[v.group] || 'overig']));

/**
 * De soort verpakking van een monster. Zonder code is een ongeroerd monster een steekbus --
 * dat staat er dan wel bij -- en de rest onbekend: een glazen pot aannemen zou een emmer die
 * niet is ingevuld stil bij de potten zetten.
 */
export function packagingOf(monster) {
  const code = monster?.packingType;
  if (code) return PACKAGING_OF_CODE.get(String(code)) || 'overig';
  return monster?.geroerd === false ? 'steekbus' : 'onbekend';
}

/**
 * Rangorde van de intensiteit van een bijzonder bestanddeel, voor 'vanaf matig'.
 *
 * NEN 5104 en NEN 6693 gebruiken verschillende woorden (zwak/matig/sterk tegenover weinig/
 * matig/veel); ze komen hier op dezelfde trap, zodat één filter op beide boringen werkt.
 */
const INTENSITY_RANK = {
  geen: 0,
  sporen: 1, 'zeer weinig': 1, 'een beetje': 1, resten: 1, spikkels: 1,
  zwak: 2, weinig: 2, brokken: 2, laagjes: 2, lensjes: 2, insluitingen: 2,
  matig: 3,
  sterk: 4, veel: 4, uiterst: 4, volledig: 4, overvloedig: 4, dominant: 4,
};

export const MIN_INTENSITY_OPTIONS = [
  { value: 'zwak', rank: 2, label: 'Vanaf zwak / weinig' },
  { value: 'matig', rank: 3, label: 'Vanaf matig' },
  { value: 'sterk', rank: 4, label: 'Vanaf sterk / veel' },
];

export function intensityRank(label) {
  return INTENSITY_RANK[String(label || '').trim().toLowerCase()] ?? 0;
}

/** De drie families waarop je filtert; de textuurfamilies vallen daar zo onder. */
export const FAMILY_LABELS = { antropogeen: 'Antropogeen', organisch: 'Organisch', mineraal: 'Mineraal' };
const FAMILY_OF_TEXTURE = {
  puin: 'antropogeen', asfalt: 'antropogeen', slakken: 'antropogeen', afval: 'antropogeen',
  organisch: 'organisch', veen: 'organisch',
  gesteente: 'mineraal', mineraal: 'mineraal', grind: 'mineraal', zand: 'mineraal', klei: 'mineraal', silt: 'mineraal', slib: 'mineraal',
};

/**
 * De groep van een bestanddeel in de keuzelijst 'bijzondere bestanddelen'. Met 90+ soorten
 * zoek je anders lang; asfalt is een eigen groep omdat het soms een derde van de lijst is.
 */
const BZB_GROEP_VAN_TEXTUUR = {
  asfalt: 'Asfalt', puin: 'Puin en bouwstoffen', slakken: 'Puin en bouwstoffen', afval: 'Afval',
  organisch: 'Organisch', veen: 'Organisch',
  gesteente: 'Gesteenten en mineralen', mineraal: 'Gesteenten en mineralen', grind: 'Gesteenten en mineralen',
  zand: 'Gesteenten en mineralen', klei: 'Gesteenten en mineralen', silt: 'Gesteenten en mineralen', slib: 'Gesteenten en mineralen',
};
export const BZB_GROEP_VOLGORDE = ['Puin en bouwstoffen', 'Asfalt', 'Afval', 'Organisch', 'Gesteenten en mineralen', 'Overig'];
export function bzbGroep(type) {
  return BZB_GROEP_VAN_TEXTUUR[classifyBzbLabel(type || '')] || 'Overig';
}

/** Familie van één bestanddeel. Is de herkomst als antropogeen vastgelegd, dan wint die. */
export function bzbFamily(type, origin) {
  if (/^(an|antropogeen)$/i.test(String(origin || ''))) return 'antropogeen';
  return FAMILY_OF_TEXTURE[classifyBzbLabel(type || '')] || null;
}

const isAsbestos = (type) => /asbest/i.test(type || '');
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const reverse = (map) => new Map([...map].map(([label, code]) => [code, label]));
const ODOR_LABEL = reverse(GEUR_TYPE);
const OIL_LABEL = reverse(OLIE_WATER_REACTIE);
const NO_ODOR = new Set(['', 'NA', 'niks aangetroffen', 'geen']);
const NO_INTENSITY = new Set(['0', 'geen']);

/** Geur van een laag als leesbaar woord, of null als er niets geroken is. */
function odorOf(raw) {
  const type = String(raw?.geurType ?? '').trim();
  const intensity = String(raw?.geurIntensity ?? '').trim().toLowerCase();
  if (NO_ODOR.has(type) || NO_INTENSITY.has(intensity)) return null;
  return `geur (${ODOR_LABEL.get(type) || type})`;
}

/** Olie-waterreactie, of null bij geen reactie. */
function oilOf(raw) {
  const v = String(raw?.oilWaterReaction ?? '').trim();
  if (!v || NO_INTENSITY.has(v.toLowerCase())) return null;
  return `olie-water ${(OIL_LABEL.get(v) || v).toLowerCase()}`;
}

/** Zelfde id als het mengmonsterpaneel een potje geeft; anders herkent het mandje hem niet. */
export function potIdOf(boring, monster) {
  return monster.id || monster.localId || `${boring.id}:${monster.name}`;
}

/** Ligging van een traject ten opzichte van de grondwaterstand; null als die onbekend is. */
export function positionOf(from, to, gws) {
  if (gws == null || !Number.isFinite(gws)) return null;
  if (to <= gws) return 'above';
  if (from >= gws) return 'below';
  return 'across';
}

export const POSITION_LABELS = {
  above: 'boven grondwater', below: 'onder grondwater', across: 'door de grondwaterstand',
};

/**
 * Een boring teruggebracht tot wat het overzicht nodig heeft: de lagen met hun beschrijving,
 * bestanddelen, waarnemingen en ligging, en de potjes met de lagen waar ze uit komen.
 */
export function overviewOf(boring) {
  const data = buildBoorprofielData(boring) || { layers: [] };
  const model = BoringModel.fromFlat(boring);
  // Zelfde volgorde als het model (op bovendiepte), zodat laag i overal dezelfde laag is.
  const raws = [...(boring.layers || [])].sort((a, b) => (Number(a.upperDepth) || 0) - (Number(b.upperDepth) || 0));
  const gwsRaw = boring.gwsFound === false ? null : boring.gwsCmMv;
  const gws = gwsRaw == null || gwsRaw === '' ? null : Number(gwsRaw);
  const name = boring.name || 'Naamloos';

  const layers = data.layers.map((l, i) => {
    const raw = raws[i] || {};
    const from = Number(l.from) || 0, to = Number(l.to) || 0;
    const soil = l.soilLabel || '';
    const bzb = (model.layers?.[i]?.bzb || [])
      .map((b, j) => {
        const type = capitalize(data.layers[i].bzb?.[j]?.label || b.type || b.code || '');
        const intensity = getBzbGradationLabel(b.gradation) || '';
        return {
          type, intensity, rank: intensityRank(intensity),
          // Een laag zonder grondsoort met 'asfalt' zonder intensiteit ís asfalt: zo beschrijft
          // het veld een verhardingslaag, en zo hoort het filter hem ook te lezen.
          fills: bzbFillsLayer(b.gradation) || (!soil && !intensity),
          family: bzbFamily(type, b.origin),
          kind: classifyBzbLabel(type),
        };
      })
      .filter((b) => b.type);
    const admixtures = (l.admix || []).map((a) => a.label).filter(Boolean);
    // Bijmenging mét intensiteit ('zwak siltig'): daarop wordt gemengd, niet alleen op de soort.
    const admixProfile = (l.admix || []).filter((a) => a.label)
      .map((a) => [a.grad, a.label].filter(Boolean).join(' ')).sort();
    const filling = bzb.filter((z) => z.fills).map((z) => z.type);
    return {
      key: `${boring.id}:${i}`,
      boringId: boring.id, boringName: name, from, to,
      soil, admixtures, admixProfile, bzb,
      description: capitalize([soil || filling.join(' en ').toLowerCase() || 'grondsoort onbekend',
        ...(l.admix || []).map((a) => [a.grad, a.label].filter(Boolean).join(' '))].filter(Boolean).join(', ')),
      odor: odorOf(raw), oil: oilOf(raw),
      position: positionOf(from, to, gws),
      pots: [],
    };
  });

  const pots = (boring.monsters || []).filter((m) => !m.deletedAt).map((m) => {
    const from = Number(m.upperDepth) || 0, to = Number(m.lowerDepth) || 0;
    return {
      id: potIdOf(boring, m), monster: m, boring,
      name: m.name || m.identificatie || 'monster', boringName: name,
      from, to,
      disturbed: m.geroerd !== false,
      packaging: packagingOf(m),
      packagingLabel: monsterVerpakkingLabel(m.packingType) || PACKAGING_LABELS[packagingOf(m)],
      matrix: /water|fles/i.test(String(m.type || '')) ? 'grondwater' : 'grond',
      position: positionOf(from, to, gws),
      layers: layers.filter((l) => l.from < to && l.to > from),
    };
  });
  for (const p of pots) for (const l of p.layers) l.pots.push(p);

  return { id: boring.id, name, gws, boring, layers, pots };
}

// ─── Grondwater ────────────────────────────────────────────────────────────
//
// Een watermonster staat als blok op de boring (`watermonsters`): een bemonstering van een
// peilbuis, met flessen. Voor de labopdracht is één watermonster één analysemonster -- grondwater
// wordt niet gemengd -- en elke fles een deelmonster met zijn eigen barcode. De diepte is het
// filter van de peilbuis, niet een grondlaag.

/** 'water' is de oude naam; de labkoppeling en de monstertypes kennen 'grondwater'. */
export const isWaterMatrix = (matrix) => matrix === 'water' || matrix === 'grondwater';

/** Het id van een fles als potje: vast, zodat hij na herladen weer bij hetzelfde watermonster hoort. */
export const bottlePotId = (watermonsterId, index) => `wm:${watermonsterId}:${index}`;

/** Hoort dit potje bij dit watermonster? */
export const isBottleOf = (potId, watermonsterId) => String(potId || '').startsWith(`wm:${watermonsterId}:`);

/**
 * De watermonsters van een boring, elk met zijn peilbuis, filterdiepte (cm -mv) en flessen als
 * potjes in de vorm van het mandje.
 */
export function watermonstersOf(boring) {
  const peilbuizen = (boring?.peilbuizen || []).filter((p) => !p.deletedAt);
  return (boring?.watermonsters || []).map((wm) => {
    const pb = peilbuizen.find((p) => p.id === wm.peilbuisId) || (peilbuizen.length === 1 ? peilbuizen[0] : null);
    const van = Number(pb?.filterTopCm ?? pb?.filterFromCm);
    const tot = Number(pb?.filterBottomCm ?? pb?.filterToCm);
    const filter = Number.isFinite(van) && Number.isFinite(tot) ? { van, tot } : null;
    const potjes = (wm.bottles || []).map((bottle, i) => ({
      id: bottlePotId(wm.id || wm.name, i),
      naam: `${wm.name || 'watermonster'} fles ${bottle.name || i + 1}`,
      boringId: boring.id,
      boringNaam: boring.name || 'Naamloos',
      verpakking: bottle.bottleType || 'FL',
      barcode: bottle.barcode || null,
      datum: wm.date || null,
      van: filter?.van ?? 0,
      tot: filter?.tot ?? 0,
      matrix: 'grondwater',
      gefiltreerd: !!bottle.filtered,
      watermonster: wm.name || null,
    }));
    return { wm, peilbuis: pb, filter, potjes };
  });
}

// ─── Filter ────────────────────────────────────────────────────────────────

/*
 * Het monstertraject is minDepthCm–maxDepthCm, in cm onder maaiveld: een laag (of potje) doet
 * mee als hij dat traject overlapt. 'Vanaf 5 cm' laat zo een asfaltlaag van 0–5 cm weg, en
 * 'tot 160 cm' houdt een laag van 150–200 cm erin.
 */
export const EMPTY_FILTER = Object.freeze({
  include: [], exclude: [], mode: 'any', minIntensity: '', families: [], form: '', minDepthCm: null, maxDepthCm: null,
  sampledOnly: true, soils: [], admixtures: [], admixtureMode: 'any', groundwater: [], observation: [],
  observationMode: 'any', boringIds: [], packaging: [],
});

/**
 * Grondwater en waarneming zijn lijsten (meerdere aanvinken in de keuzelijst). Flip en de
 * eenvoudige woordherkenning geven nog één waarde ('above'); die wordt hier een lijst.
 */
export const alsLijst = (v) => (Array.isArray(v) ? v : (v ? [v] : []));

/** Een filter in de vorm die de keuzelijsten verwachten: lijsten en een modus per filter. */
export function normalizeFilter(f) {
  return {
    ...EMPTY_FILTER, ...f,
    groundwater: alsLijst(f?.groundwater),
    observation: alsLijst(f?.observation),
    admixtureMode: f?.admixtureMode === 'all' ? 'all' : 'any',
    observationMode: f?.observationMode === 'all' ? 'all' : 'any',
  };
}

/** Filtert er iets? 'Alleen bemonsterd' telt niet: dat versmalt wat je ziet, niet wat voldoet. */
export function isFilterActive(f) {
  return !!(f.include.length || f.exclude.length || f.minIntensity || f.form || f.families.length
    || f.minDepthCm != null || f.maxDepthCm != null || f.soils.length || f.admixtures.length
    || alsLijst(f.groundwater).length || alsLijst(f.observation).length
    || f.boringIds.length || f.packaging?.length);
}

/** Past de verpakking van dit potje bij het filter? Zonder keuze altijd. */
export function potPassesPackaging(pot, f) {
  return !f.packaging?.length || f.packaging.includes(pot.packaging);
}

const lower = (s) => String(s).toLowerCase();
const orList = (items) => items.map(lower).join(' of ');

function bzbPasses(z, f) {
  if (f.minIntensity && !z.fills && z.rank < intensityRank(f.minIntensity)) return false;
  if (f.form === 'fills' && !z.fills) return false;
  if (f.form === 'admix' && z.fills) return false;
  if (f.families.length && !f.families.includes(z.family)) return false;
  return true;
}

function bzbWhyNot(z, f) {
  const t = lower(z.type);
  if (f.minIntensity && !z.fills && z.rank < intensityRank(f.minIntensity)) {
    return `${t} is ${z.intensity || 'zonder intensiteit'}, minder dan ${f.minIntensity}`;
  }
  if (f.form === 'fills' && !z.fills) return `${t} is alleen bijmenging`;
  if (f.form === 'admix' && z.fills) return `laag bestaat uit ${t}`;
  return `${t} is niet ${orList(f.families.map((x) => FAMILY_LABELS[x] || x))}`;
}

/**
 * Waarom een laag niet aan het filter voldoet, in een paar woorden; null als hij voldoet.
 *
 * Eén reden en niet alle: de kaartjes moeten leesbaar blijven, en de eerste reden is meestal
 * ook de reden die je wilde weten. De volgorde loopt van grof (diepte, uitsluiting) naar fijn.
 */
export function rejectReason(layer, f) {
  if (f.minDepthCm != null && layer.to <= f.minDepthCm) return `eindigt op ${layer.to} cm`;
  if (f.maxDepthCm != null && layer.from >= f.maxDepthCm) return `begint op ${layer.from} cm`;
  if (f.packaging?.length && !layer.pots.some((p) => potPassesPackaging(p, f))) {
    return `geen ${orList(f.packaging.map((x) => PACKAGING_LABELS[x] || x))}`;
  }
  const banned = layer.bzb.find((z) => f.exclude.includes(z.type));
  if (banned) return `bevat ${lower(banned.type)}`;
  if (f.soils.length && !f.soils.includes(layer.soil)) return `hoofdgrondsoort is ${lower(layer.soil || 'onbekend')}`;
  if (f.admixtures.length) {
    // 'Allemaal': elke gekozen bijmenging; anders minstens één.
    if (f.admixtureMode === 'all') {
      const mist = f.admixtures.find((a) => !layer.admixtures.includes(a));
      if (mist) return `niet ${lower(mist)}`;
    } else if (!layer.admixtures.some((a) => f.admixtures.includes(a))) return `niet ${orList(f.admixtures)}`;
  }
  const grondwater = alsLijst(f.groundwater);
  if (grondwater.length) {
    if (!layer.position) return 'grondwaterstand onbekend';
    if (!grondwater.includes(layer.position)) return `ligt ${POSITION_LABELS[layer.position]}`;
  }
  const waarneming = alsLijst(f.observation);
  if (waarneming.length) {
    const waarom = {
      odor: () => (layer.odor ? null : 'geen geur waargenomen'),
      oil: () => (layer.oil ? null : 'geen olie waargenomen'),
      none: () => (layer.odor || layer.oil ? (layer.oil ? 'olie waargenomen' : 'geur waargenomen') : null),
    };
    const redenen = waarneming.map((w) => waarom[w]?.() ?? null);
    // 'Allemaal': elke gekozen waarneming moet kloppen; anders minstens één.
    if (f.observationMode === 'all' ? redenen.some(Boolean) : redenen.every(Boolean)) return redenen.find(Boolean);
  }
  if (f.include.length) {
    const has = (t) => layer.bzb.some((z) => z.type === t && bzbPasses(z, f));
    if (f.mode === 'all') {
      const missing = f.include.find((t) => !has(t));
      if (!missing) return null;
      const z = layer.bzb.find((x) => x.type === missing);
      return z ? bzbWhyNot(z, f) : `geen ${lower(missing)}`;
    }
    if (f.include.some(has)) return null;
    const z = layer.bzb.find((x) => f.include.includes(x.type));
    return z ? bzbWhyNot(z, f) : `geen ${orList(f.include)}`;
  }
  if ((f.minIntensity || f.form || f.families.length) && !layer.bzb.some((z) => bzbPasses(z, f))) {
    return layer.bzb.length ? bzbWhyNot(layer.bzb[0], f) : 'geen bijzonder bestanddeel';
  }
  return null;
}

/** Valt het potje (deels) binnen het monstertraject? */
export function potInDepthRange(pot, f) {
  return (f.minDepthCm == null || pot.to > f.minDepthCm) && (f.maxDepthCm == null || pot.from < f.maxDepthCm);
}

/** De lagen die je ziet: met 'alleen bemonsterd' alleen lagen waar een potje uit komt. */
export function visibleLayers(overview, f) {
  return f.sampledOnly ? overview.layers.filter((l) => l.pots.length) : overview.layers;
}

/** De potjes die het filter oplevert: uit een laag die voldoet, in een boring die meedoet. */
export function matchingPots(overviews, f) {
  const seen = new Map();
  for (const o of overviews) {
    if (f.boringIds.length && !f.boringIds.includes(o.id)) continue;
    for (const l of o.layers) {
      if (rejectReason(l, f)) continue;
      for (const p of l.pots) if (potPassesPackaging(p, f) && potInDepthRange(p, f)) seen.set(p.id, p);
    }
  }
  return [...seen.values()];
}

// ─── Welke boringen meedoen ────────────────────────────────────────────────
//
// Los van het lagenfilter hierboven: dit bepaalt welke boringen er op de kaart en in de
// selectie staan, het lagenfilter welke lagen en potjes je daarbinnen kiest. Alleen boringen
// tot minstens 3 m (dit filter) is iets anders dan alleen het traject 1,5–3 m (het monstertraject).

export const EMPTY_BORING_FILTER = Object.freeze({
  // Geotechnische boringen zonder potjes horen niet bij het samenstellen van mengmonsters.
  withSamplesOnly: true,
  minEndDepthCm: null,
});

/** Einddiepte van een boring in cm –mv: de onderkant van de diepste laag of het diepste potje. */
export function endDepthOf(overview) {
  const ends = [...overview.layers.map((l) => l.to), ...overview.pots.map((p) => p.to)];
  if (ends.length) return Math.max(...ends);
  const d = Number(overview.boring?.depth);
  return Number.isFinite(d) ? d : 0;
}

/** Doet deze boring mee onder het boringfilter? */
export function boringPassesFilter(overview, bf) {
  if (bf.withSamplesOnly && !overview.pots.length) return false;
  if (bf.minEndDepthCm != null && endDepthOf(overview) < bf.minEndDepthCm) return false;
  return true;
}

// ─── Mengregels ────────────────────────────────────────────────────────────

/**
 * Hoe verdacht een potje is, naar de ergste laag waar het uit komt.
 *
 * Asbest gaat altijd apart: één asbestverdachte pot in een mengmonster maakt de hele uitslag
 * onbruikbaar. Bodemvreemd telt vanaf 'zwak' of als de laag eruit bestaat; sporen puin maken
 * een laag nog niet verdacht.
 */
const SUSPICION_ORDER = ['onverdacht', 'zintuiglijk', 'bodemvreemd', 'asbest'];
export const SUSPICION_LABELS = {
  asbest: 'asbestverdacht', bodemvreemd: 'met bodemvreemd materiaal',
  zintuiglijk: 'met geur of olie', onverdacht: 'onverdacht',
};

function layerSuspicion(l) {
  if (l.bzb.some((z) => isAsbestos(z.type))) return 'asbest';
  if (l.bzb.some((z) => z.family === 'antropogeen' && (z.fills || z.rank >= 2))) return 'bodemvreemd';
  if (l.odor || l.oil) return 'zintuiglijk';
  return 'onverdacht';
}

export function potSuspicion(pot) {
  return pot.layers.map(layerSuspicion)
    .reduce((worst, s) => (SUSPICION_ORDER.indexOf(s) > SUSPICION_ORDER.indexOf(worst) ? s : worst), 'onverdacht');
}

/** De laag waar het grootste deel van het potje uit komt. */
function mainLayerOf(pot) {
  let best = null, bestOverlap = -1;
  for (const l of pot.layers) {
    const overlap = Math.min(l.to, pot.to) - Math.max(l.from, pot.from);
    if (overlap > bestOverlap) { best = l; bestOverlap = overlap; }
  }
  return best;
}

/** Het materiaal van een potje: de grondsoort van die laag, of het bestanddeel dat die laag is. */
export function potMaterial(pot) {
  const best = mainLayerOf(pot);
  if (!best) return 'Onbekend';
  return best.bzb.find((z) => z.fills)?.type || capitalize(best.soil) || 'Onbekend';
}

/**
 * Bodem, asfalt of puin. Asfalt en puin worden anders onderzocht dan grond, dus die gaan nooit
 * samen met een bodemmonster in één mengmonster.
 */
export const POT_KIND_LABELS = { bodem: 'bodem', asfalt: 'asfalt', puin: 'puin' };

export function potKind(pot) {
  const fill = mainLayerOf(pot)?.bzb.find((z) => z.fills);
  if (fill?.kind === 'asfalt') return 'asfalt';
  if (fill?.kind === 'puin') return 'puin';
  return 'bodem';
}

/** De bijmengingen met intensiteit van het potje ('zwak siltig, matig humeus'); leeg als er geen zijn. */
export function potAdmixtures(pot) {
  return (mainLayerOf(pot)?.admixProfile || []).join(', ');
}

/** Wat er samen mag: zelfde grondsoort én zelfde bijmengingen met dezelfde intensiteit. */
export function potComposition(pot) {
  const admix = potAdmixtures(pot);
  return admix ? `${potMaterial(pot)}, ${admix}` : potMaterial(pot);
}

const unique = (a) => [...new Set(a)].sort((x, y) => x.localeCompare(y, 'nl'));

/**
 * Bovengrond (0 tot 0,5 m), ondergrond (0,5 tot 2 m) of dieper, naar het midden van de pot.
 * Bovengrond en ondergrond worden apart beoordeeld; een mengmonster dat ze door elkaar haalt,
 * zegt over geen van beide iets.
 */
export const DEPTH_ZONES = [
  { id: 'top', max: 50, label: 'bovengrond (0–0,5 m)', short: 'bovengrond' },
  { id: 'sub', max: 200, label: 'ondergrond (0,5–2 m)', short: 'ondergrond' },
  { id: 'deep', max: Infinity, label: 'dieper dan 2 m', short: 'dieper dan 2 m' },
];

export function depthZoneOf(pot) {
  const mid = (pot.from + pot.to) / 2;
  return DEPTH_ZONES.find((z) => mid <= z.max) || DEPTH_ZONES.at(-1);
}

/**
 * Meer potten dan dit in één mengmonster verdunt een plek met verontreiniging tot onder de
 * meetgrens. Een grotere groep wordt in gelijke delen gesplitst.
 */
export const MAX_POTS_PER_SAMPLE = 4;

/**
 * Hoeveel potten er in één mengmonster mogen. Ligt een potje in een deellocatie met een
 * strategie, dan geldt het maximum van de norm (pot.deellocatie.max, zie maxPotsPerMixedSample
 * in @mv/models: ONV 10, VEP 5, VEP-OO 3, VED 4); anders MAX_POTS_PER_SAMPLE. Bij meerdere het
 * strengste.
 */
export function maxPotsFor(pots) {
  const maxes = (pots || []).map((p) => p?.deellocatie?.max).filter((n) => Number.isFinite(n));
  return maxes.length ? Math.min(...maxes) : MAX_POTS_PER_SAMPLE;
}

/** De strategie achter het maximum, voor de melding ('bij VEP'). */
function strategieVan(pots) {
  const s = (pots || []).map((p) => p?.deellocatie).filter((d) => d?.max != null)
    .sort((a, b) => a.max - b.max)[0];
  return s?.strategie || null;
}

/** De melding bij te veel potten, of null. Los, want het mandje telt ook potjes zonder bekende lagen. */
export function tooManyPotsWarning(count, max = MAX_POTS_PER_SAMPLE, strategie = null) {
  return count > max
    ? `${count} potten; ${strategie ? `bij ${strategie} mogen` : 'er mogen'} er hoogstens ${max} in één mengmonster`
    : null;
}

/**
 * Waar je op moet letten als je deze potjes samen mengt. Leeg als het mag.
 */
export function mixingWarnings(pots) {
  if (pots.length < 2) return [];
  const out = [];
  const differs = (fn) => new Set(pots.map(fn)).size > 1;
  if (differs((p) => p.matrix)) out.push('grond en grondwater door elkaar');
  if (differs((p) => p.disturbed)) out.push('geroerde (schep) en ongeroerde (steekbus) monsters door elkaar');
  const tooMany = tooManyPotsWarning(pots.length, maxPotsFor(pots), strategieVan(pots));
  if (tooMany) out.push(tooMany);
  // Elke deellocatie heeft zijn eigen strategie en hypothese: niet over de grens heen mengen.
  if (differs((p) => p.deellocatie?.id || '-')) {
    out.push(`potjes uit verschillende deellocaties: ${unique(pots.map((p) => p.deellocatie?.naam || 'geen deellocatie')).join(', ')}`);
  }
  if (differs(potKind)) {
    out.push(`${unique(pots.map((p) => POT_KIND_LABELS[potKind(p)])).join(' en ')} door elkaar; asfalt en puin horen niet bij grond`);
  }
  if (differs(potMaterial)) out.push(`verschillende grondsoorten: ${unique(pots.map(potMaterial)).map(lower).join(', ')}`);
  else if (differs(potAdmixtures)) {
    // Zelfde soort bijmenging met een andere intensiteit is net zo goed een ander monster.
    const types = (p) => (mainLayerOf(p)?.admixtures || []).slice().sort().join(', ');
    const list = unique(pots.map((p) => potAdmixtures(p) || 'zonder bijmenging')).join(' / ');
    out.push(differs(types) ? `verschillende bijmengingen: ${list}` : `zelfde bijmenging, andere intensiteit: ${list}`);
  }
  if (differs((p) => depthZoneOf(p).id)) out.push('bovengrond en ondergrond door elkaar');
  if (differs((p) => p.position || '?')) out.push('lagen boven en onder het grondwater door elkaar');
  const suspicions = pots.map(potSuspicion);
  if (suspicions.includes('asbest')) out.push('een asbestverdachte laag; die hoort apart');
  else if (new Set(suspicions.map((s) => s === 'onverdacht')).size > 1) out.push('verdachte en onverdachte lagen samen');
  return out;
}

/** Op boringnaam: naast elkaar genummerde boringen liggen meestal ook naast elkaar. */
const byBoring = (a, b) => a.boringName.localeCompare(b.boringName, 'nl', { numeric: true }) || a.from - b.from;

/**
 * Een groep in `parts` zo gelijk mogelijke delen: 13 in vier is 4-3-3-3, niet 4-4-4-1. Met
 * vaste blokken van naar boven afgeronde grootte bleef er een los potje over, dat dan als
 * 'enkel monster' uit het voorstel kwam terwijl het gewoon mee kon.
 */
function splitInto(pots, parts) {
  const sorted = [...pots].sort(byBoring);
  const base = Math.floor(sorted.length / parts), extra = sorted.length % parts;
  const out = [];
  let at = 0;
  for (let i = 0; i < parts; i += 1) {
    const size = base + (i < extra ? 1 : 0);
    out.push(sorted.slice(at, at + size));
    at += size;
  }
  return out.filter((g) => g.length);
}

/**
 * Potjes die volgens de mengregels samen mogen, per groep (nog niet gesplitst). Asbestverdacht
 * krijgt elk een eigen groep: dat gaat nooit samen.
 */
function ruleGroups(pots) {
  const groups = new Map();
  for (const p of pots) {
    const s = potSuspicion(p);
    const key = s === 'asbest' ? `asbest|${p.id}`
      : [p.deellocatie?.id || '-', p.matrix, potKind(p), potComposition(p), depthZoneOf(p).id, p.position || '?', p.disturbed, s].join('|');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }
  return [...groups.values()];
}

/**
 * Hoeveel mengmonsters er van deze potjes kunnen komen zonder een mengregel te breken.
 *   min  elke groep in zo weinig mogelijk delen van hoogstens MAX_POTS_PER_SAMPLE
 *   max  elke groep in zo veel mogelijk delen van minstens twee potten
 *   singles  potjes die met niets gemengd kunnen worden (die tellen in geen van beide mee)
 */
export function mixedSampleRange(pots) {
  let min = 0, max = 0, singles = 0;
  for (const g of ruleGroups(pots)) {
    if (g.length < 2) { singles += 1; continue; }
    min += Math.ceil(g.length / maxPotsFor(g));
    max += Math.floor(g.length / 2);
  }
  return { min, max, singles };
}

/**
 * Een voorstel voor mengmonsters volgens de mengregels: alleen potten samen met hetzelfde
 * materiaal, uit dezelfde zone (boven- of ondergrond), met dezelfde ligging ten opzichte van
 * het grondwater, dezelfde monstername (geroerd of steekbus) en dezelfde verdenking, en niet
 * meer dan de norm toestaat (maxPotsFor: per strategie van de deellocatie). Asbestverdacht gaat
 * altijd los, en potjes uit verschillende deellocaties komen nooit samen.
 *
 * Met `target` probeert het voorstel op dat aantal mengmonsters uit te komen, door groepen
 * verder te splitsen (nooit kleiner dan twee potten). Minder dan het minimum kan niet zonder
 * een regel te breken; dan blijft het bij het minimum, en zegt de aanroeper dat erbij.
 *
 * @param {Array} pots          de potjes waaruit voorgesteld wordt (al ingedeelde eruit laten)
 * @param {number} lastNumber   het hoogste MM-nummer dat al bestaat
 * @param {number|null} target  gewenst aantal mengmonsters, of null voor zo weinig mogelijk
 */
export function proposeMixedSamples(pots, lastNumber = 0, target = null) {
  const groups = ruleGroups(pots).map((g) => ({ pots: g, parts: g.length < 2 ? 1 : Math.ceil(g.length / maxPotsFor(g)) }));
  const mixable = groups.filter((g) => g.pots.length >= 2);
  let total = mixable.reduce((n, g) => n + g.parts, 0);
  // Verder splitsen waar de delen het grootst zijn, tot het gewenste aantal of tot het niet meer kan.
  while (target != null && total < target) {
    const next = mixable
      .filter((g) => g.pots.length / (g.parts + 1) >= 2)
      .sort((a, b) => b.pots.length / b.parts - a.pots.length / a.parts)[0];
    if (!next) break;
    next.parts += 1;
    total += 1;
  }

  let n = lastNumber;
  return groups
    .flatMap((g) => splitInto(g.pots, g.parts))
    .sort((a, b) => b.length - a.length)
    .map((g) => {
      const first = g[0], s = potSuspicion(first), single = g.length === 1;
      const where = [depthZoneOf(first).label, first.position ? POSITION_LABELS[first.position] : ''].filter(Boolean).join(', ');
      const dl = first.deellocatie;
      return {
        code: single ? null : `MM${String(++n).padStart(2, '0')}`,
        title: `${dl ? `${dl.naam}${dl.strategie ? ` (${dl.strategie})` : ''} · ` : ''}${potComposition(first)}; ${where}, ${SUSPICION_LABELS[s]}`,
        // Voor de gebruiker: 'Zand, bovengrond'. De volledige titel staat in de tooltip.
        short: `${potMaterial(first)}, ${depthZoneOf(first).short}`,
        asbestos: s === 'asbest',
        pots: g,
        reason: single
          ? (s === 'asbest' ? 'Asbestverdacht: apart analyseren, niet mengen.'
            : 'Geen vergelijkbare pot om mee te mengen; als enkel monster analyseren.')
          : `${g.length} potten, ${first.disturbed ? 'allemaal geroerd (schep)' : 'allemaal ongeroerd (steekbus)'}, zelfde grondsoort en bijmengingen, zelfde zone en ligging${dl ? `, allemaal uit ${dl.naam}` : ''}.`,
        single,
      };
    });
}

// ─── Dekking: zit elke laag van de locatie in een mengmonster? ─────────────

/**
 * Het soort laag, zoals de mengregels het zien: materiaal met bijmengingen, zone, ligging,
 * verdenking en of het asfalt, puin of bodem is. Twee lagen van hetzelfde soort kunnen door
 * één mengmonster vertegenwoordigd worden; een soort zonder mengmonster is niet onderzocht.
 */
export function layerTypeOf(layer) {
  const pseudo = { from: layer.from, to: layer.to, layers: [layer], position: layer.position };
  const s = potSuspicion(pseudo);
  const where = [depthZoneOf(pseudo).label, layer.position ? POSITION_LABELS[layer.position] : ''].filter(Boolean).join(', ');
  return {
    key: [potKind(pseudo), potComposition(pseudo), depthZoneOf(pseudo).id, layer.position || '?', s].join('|'),
    label: `${potComposition(pseudo)}; ${where}, ${SUSPICION_LABELS[s]}`,
    short: `${potMaterial(pseudo)}, ${depthZoneOf(pseudo).short}`,
  };
}

/**
 * Welke soorten lagen van de locatie in een mengmonster zitten, en welke niet.
 *
 * Een soort telt als gedekt zodra één potje eruit in een mengmonster zit (of in het voorstel,
 * als je dat meegeeft): je hoeft niet elke laag te mengen, wel elke soort laag te onderzoeken.
 * Soorten zonder enig potje staan apart -- die kún je niet dekken zonder terug het veld in.
 *
 * @param {Array} overviews          overviewOf() van elke boring van de locatie
 * @param {(potId:string)=>boolean} isCovered   zit dit potje in een mengmonster (of voorstel)?
 */
export function layerCoverage(overviews, isCovered) {
  const types = new Map();
  for (const o of overviews) {
    for (const l of o.layers) {
      // Grondwater is geen laag; een potje grondwater dekt geen grondlaag.
      const t = layerTypeOf(l);
      if (!types.has(t.key)) types.set(t.key, { ...t, layers: [], pots: [], covered: false });
      const entry = types.get(t.key);
      entry.layers.push(l);
      for (const p of l.pots) {
        if (isWaterMatrix(p.matrix)) continue;
        entry.pots.push(p);
        if (isCovered(p.id)) entry.covered = true;
      }
    }
  }
  const all = [...types.values()];
  return {
    covered: all.filter((t) => t.covered),
    missing: all.filter((t) => !t.covered && t.pots.length),
    unsampled: all.filter((t) => !t.pots.length),
  };
}

// ─── Assistent zonder verbinding ───────────────────────────────────────────

/**
 * Een eenvoudige woordherkenning voor als de assistent niet bereikbaar is. Herkent de namen uit
 * het overzicht en een handvol vaste woorden ('zonder', 'boven grondwater', 'bovengrond'); de
 * rest laat hij liggen. Liever een half filter dat je ziet dan een foutmelding en niets.
 *
 * @param {string} text
 * @param {object} filter   het huidige filter
 * @param {{bzbTypes:string[], soils:string[], admixtures:string[], borings:{id:string,name:string}[]}} options
 * @returns {{action:'filter'|'propose'|'undo'|'clear'|'check', filter:object|null, count:number|null}}
 */
export function parseQuestionLocally(text, filter, options) {
  const t = ` ${String(text || '').toLowerCase()} `;
  if (/^\s*(wis|reset)/.test(t)) return { action: 'clear', filter: { ...EMPTY_FILTER, sampledOnly: filter.sampledOnly } };
  if (/vorige|ongedaan|terug naar/.test(t)) return { action: 'undo', filter: null };
  if (/controleer|gedekt|dekking|vertegenwoordigd/.test(t)) return { action: 'check', filter: null, count: null };
  const action = /voorstel|stel .*voor|mengmonster|verdeel/.test(t) ? 'propose' : 'filter';
  const escape = (s) => s.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Een naam als los woord; 'puin' mag niet in 'puingranulaat' gevonden worden.
  const word = (name, before = '', after = '') => new RegExp(`${before}\\b${escape(name)}${after}\\b`).test(t);
  const n = JSON.parse(JSON.stringify(filter));
  for (const type of options.bzbTypes) {
    if (!word(type)) continue;
    if (word(type, '(zonder|geen)[a-z ,]*')) {
      if (!n.exclude.includes(type)) n.exclude.push(type);
      n.include = n.include.filter((x) => x !== type);
    } else if (!n.include.includes(type)) n.include.push(type);
  }
  const soils = options.soils.filter((s) => !options.bzbTypes.some((b) => b.toLowerCase() === s.toLowerCase())
    && word(s, '', '(lagen|laag)?'));
  if (soils.length) n.soils = soils;
  const admixtures = options.admixtures.filter((a) => word(a));
  if (admixtures.length) n.admixtures = admixtures;
  const m = t.match(/vanaf (zwak|matig|sterk)/); if (m) n.minIntensity = m[1];
  if (/bestaat uit|bestaan uit|hele laag/.test(t)) n.form = 'fills';
  if (/organisch/.test(t)) n.families = ['organisch'];
  if (/antropogeen|bodemvreemd/.test(t)) n.families = ['antropogeen'];
  if (/boven (het )?grondwater/.test(t)) n.groundwater = 'above';
  if (/onder (het )?grondwater/.test(t)) n.groundwater = 'below';
  if (/(zonder|geen) (geur|olie)/.test(t)) n.observation = 'none';
  else if (/\bolie\b/.test(t)) n.observation = 'oil';
  else if (/\bgeur\b/.test(t)) n.observation = 'odor';
  const d = t.match(/(?:tot|bovenste)\s*(\d+(?:[,.]\d+)?)\s*(m|cm)\b/);
  if (d) { const v = parseFloat(d[1].replace(',', '.')); n.maxDepthCm = d[2] === 'm' ? Math.round(v * 100) : v; }
  if (/bovenste meter/.test(t)) n.maxDepthCm = 100;
  if (/bovengrond/.test(t)) n.maxDepthCm = 50;
  const borings = options.borings.filter((b) => word(b.name)).map((b) => b.id);
  if (borings.length) n.boringIds = borings;
  if (/alle lagen|ook onbemonsterd|hele profiel/.test(t)) n.sampledOnly = false;
  if (/allemaal|samen|beide/.test(t) && n.include.length > 1) n.mode = 'all';
  const count = t.match(/(\d{1,3})\s*mengmonsters?/);
  return { action, filter: n, count: count ? Number(count[1]) : null };
}
